import { useContext, useEffect, useState, useCallback } from "react";
import ThemeContext from "../../lib/theme-context";
import AuthContext from "../../lib/auth-context";
import { getSelectedAccountId } from "../../utils/accountUtils";
import { processEquityCurveData } from "./utils/dataProcessor";
import EquityCurveChart from "./EquityCurveChart";

const EquityCurve = ({ calculateFields, selectedTimeframe: propSelectedTimeframe, showMetric: propShowMetric }) => {
  const theme = useContext(ThemeContext);
  const auth = useContext(AuthContext);

  const [selectedTimeframe, setSelectedTimeframe] = useState(propSelectedTimeframe || "Weekly");
  const [headingText, setHeadingText] = useState(new Date().getFullYear().toString());
  const [showMetric, setShowMetric] = useState(propShowMetric || "percentage");
  const [showGross, setShowGross] = useState(() => localStorage.getItem("show-gross") === "true");
  const [chartData, setChartData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [mode, setMode] = useState(localStorage.getItem("equity-mode") || null);

  // Sync with props when they change
  useEffect(() => {
    if (propSelectedTimeframe) setSelectedTimeframe(propSelectedTimeframe);
  }, [propSelectedTimeframe]);

  useEffect(() => {
    if (propShowMetric) setShowMetric(propShowMetric);
  }, [propShowMetric]);

  useEffect(() => {
    if (!localStorage.getItem("year")) {
      const currentYear = new Date().getFullYear().toString();
      localStorage.setItem("year", currentYear);
      setHeadingText(currentYear);
    } else {
      setHeadingText(
        localStorage.getItem("year") !== "0"
          ? localStorage.getItem("year")
          : "All"
      );
    }

    if (!localStorage.getItem("showMetric")) {
      localStorage.setItem("showMetric", "percentage");
      setShowMetric("percentage");
    }

    // Initialize mode based on current selected account
    const initializeMode = () => {
      const selectedAccountId = getSelectedAccountId();
      let newMode = null;
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      const capitalAccounts = user.capitalAccounts || [];
      const selectedAccount = capitalAccounts.find(acc => acc.id === selectedAccountId);

      if (selectedAccountId === "All") {
        newMode = null;
      } else if (selectedAccountId === "All Personal") {
        newMode = "Personal Capital";
      } else if (selectedAccountId === "All Investor") {
        newMode = "Investor Capital";
      } else if (selectedAccount) {
        if (selectedAccount.type === "Personal") {
          newMode = "Personal Capital";
        } else if (selectedAccount.type === "Investor") {
          newMode = "Investor Capital";
        }
      }
      localStorage.setItem("equity-mode", newMode || "");
      setMode(newMode);
    };

    initializeMode();

    const handleSettingsChange = () => {
      const skipPreviousYears = localStorage.getItem("skipPreviousYears") === "true";
      const currentYear = String(new Date().getFullYear());
      const currentYearSelection = localStorage.getItem("year");
      
      if (skipPreviousYears && currentYearSelection !== "0" && currentYearSelection !== currentYear) {
        localStorage.setItem("year", "0");
        setHeadingText("All");
      }
    };
    
    const handleAccountChange = (event) => {
      const selectedAccountId = event.detail?.accountId || getSelectedAccountId();

      // Determine the correct mode based on selected account
      let newMode = localStorage.getItem("equity-mode");
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      const capitalAccounts = user.capitalAccounts || [];
      const selectedAccount = capitalAccounts.find(acc => acc.id === selectedAccountId);

      if (selectedAccount) {
        if (selectedAccountId === "All") {
          newMode = null; // Show all
        } else if (selectedAccountId === "All Personal") {
          newMode = "Personal Capital";
        } else if (selectedAccountId === "All Investor") {
          newMode = "Investor Capital";
        } else if (selectedAccount.type === "Personal") {
          newMode = "Personal Capital";
        } else if (selectedAccount.type === "Investor") {
          newMode = "Investor Capital";
        }
        localStorage.setItem("equity-mode", newMode || "");
      }

      setMode(newMode);
    };

    // Listen for gross-net-changed events from dashboard
    const handleGrossNetChanged = (event) => {
      if (event.detail && event.detail.showGross !== undefined) {
        setShowGross(event.detail.showGross);
      }
    };

    window.addEventListener("gross-net-changed", handleGrossNetChanged);
    window.addEventListener('settings-changed', handleSettingsChange);
    window.addEventListener('account-changed', handleAccountChange);
    
    return () => {
      window.removeEventListener("gross-net-changed", handleGrossNetChanged);
      window.removeEventListener('settings-changed', handleSettingsChange);
      window.removeEventListener('account-changed', handleAccountChange);
    };
  }, []);

  const loadChartData = useCallback(async () => {
    setIsLoading(true);
    try {
      const selectedYear = localStorage.getItem("year") || "0";
      const selectedAccountId = getSelectedAccountId();
      const backTestId = localStorage.getItem("backtest-active");

      const data = await processEquityCurveData({
        allTrades: auth.trades,
        calculateFields,
        backtestingMode: auth.backtestingMode,
        backTestId,
        selectedAccountId,
        timeframe: selectedTimeframe,
        selectedYear,
        mode,
        showGross,
        allYearsOfTrades: auth.allYearsOfTrades
      });

      setChartData(data);
    } catch (error) {
      console.error("Error loading chart data:", error);
      setChartData([]);
    } finally {
      setIsLoading(false);
    }
  }, [auth.trades, auth.backtestingMode, auth.allYearsOfTrades, calculateFields, selectedTimeframe, mode, showGross]);

  useEffect(() => {
    loadChartData();
  }, [loadChartData, headingText]);

  return (
    <div
      style={{
        height: "100%",
        width: "100%",
        backgroundColor: theme[0] === "dark" ? "#1a1b1e" : "#f9fafb",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <div
        style={{
          width: "100%",
          padding: "0 0 0.5em 0",
          display: "flex",
          flexDirection: "column",
          flex: 1,
          minHeight: 0,
        }}
      >
    
        
        <div 
          style={{ 
            marginTop: '0.5em',  
            flex: 1, 
            minHeight: 0, 
            width: "100%", 
            opacity: isLoading ? 0.5 : 1, 
            transition: "opacity 0.2s ease", 
            paddingRight: "38px" 
          }}
        >
          {chartData.length > 0 ? (
            <EquityCurveChart 
              data={chartData} 
              showMetric={showMetric} 
              theme={theme}
            />
          ) : (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                height: "100%",
                color: theme[0] === "dark" ? "#6b7280" : "#9ca3af",
                fontSize: "14px",
              }}
            >
              {isLoading ? "Loading..." : "No data available for this period"}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EquityCurve;
